// frontend/src/pages/PublicProfile.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faTractor,
  faStore,
  faUsers,
  faArrowLeft,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faSpinner,
  faGlobe,
  faAward,
  faBuilding,
  faHandshake,
  faLightbulb,
  faClipboardList,
  faUserPlus,
  faUserCog,
  faBriefcase,
  faUserTie,
  faShieldAlt,
  faCode,
  faBox,
  faShoppingCart,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import Loader from "../components/UI/Loader";
import { getPublicProfile } from "../api";
import { useAuth } from "../context/AuthContext";
import "../styles/public-profile.scss";

// ============================================
// TEAM ROLE CONFIGURATION
// ============================================
const TEAM_ROLE_CONFIG = {
  sale_representative: {
    label: "Sales Representative",
    icon: faHandshake,
    color: "#3b82f6",
  },
  advisor: { label: "Advisor", icon: faLightbulb, color: "#f59e0b" },
  coordinator: {
    label: "Coordinator",
    icon: faClipboardList,
    color: "#8b5cf6",
  },
  assistant_advisor: {
    label: "Assistant Advisor",
    icon: faUserPlus,
    color: "#06b6d4",
  },
  assistant_coordinator: {
    label: "Assistant Coordinator",
    icon: faUserCog,
    color: "#ec4899",
  },
  manager: { label: "Manager", icon: faBriefcase, color: "#14b8a6" },
  assistant_manager: {
    label: "Assistant Manager",
    icon: faUserTie,
    color: "#f97316",
  },
  administrator: {
    label: "Administrator",
    icon: faShieldAlt,
    color: "#8b5cf6",
  },
  community_relation: {
    label: "Community & Farmer Relation",
    icon: faUsers,
    color: "#22c55e",
  },
  developer: { label: "Developer", icon: faCode, color: "#6366f1" },
};

const DEFAULT_ROLE_CONFIG = {
  label: "Team Member",
  icon: faUserCircle,
  color: "#5A5A5A",
};

const USER_TYPE_CONFIG = {
  farmer: {
    label: "Farmer",
    icon: faTractor,
    color: "#16a34a",
    bgColor: "rgba(22, 163, 74, 0.1)",
  },
  supplier: {
    label: "Supplier",
    icon: faStore,
    color: "#2563eb",
    bgColor: "rgba(37, 99, 235, 0.1)",
  },
  customer: {
    label: "Customer",
    icon: faUserCircle,
    color: "#6b7280",
    bgColor: "rgba(107, 114, 128, 0.1)",
  },
  admin: {
    label: "Administrator",
    icon: faShieldAlt,
    color: "#8b5cf6",
    bgColor: "rgba(139, 92, 246, 0.1)",
  },
  super_admin: {
    label: "Super Admin",
    icon: faAward,
    color: "#dc2626",
    bgColor: "rgba(220, 38, 38, 0.1)",
  },
  team: {
    label: "Team Member",
    icon: faUsers,
    color: "#D9A321",
    bgColor: "rgba(217, 163, 33, 0.1)",
  },
};

// ============================================
// MAIN COMPONENT
// ============================================
const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isSuperAdmin } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  // ============================================
  // FETCH PROFILE
  // ============================================
  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setError("No user ID provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getPublicProfile(userId);
      const data = response.data;

      if (data.success) {
        setProfile(data.profile);
        setIsOwner(data.isOwner || false);
        setCanEdit(data.canEdit || false);
      } else {
        setError(data.message || "Failed to load profile");
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setError(err.response?.data?.message || "Profile not found");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // ============================================
  // HELPERS
  // ============================================
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleConfig = (profileData) => {
    if (!profileData) return DEFAULT_ROLE_CONFIG;
    if (profileData.isTeamMember && profileData.teamRole) {
      return TEAM_ROLE_CONFIG[profileData.teamRole] || DEFAULT_ROLE_CONFIG;
    }
    if (profileData.userType) {
      const config = USER_TYPE_CONFIG[profileData.userType];
      if (config)
        return { label: config.label, icon: config.icon, color: config.color };
    }
    if (profileData.role) {
      const config = USER_TYPE_CONFIG[profileData.role];
      if (config)
        return { label: config.label, icon: config.icon, color: config.color };
    }
    return DEFAULT_ROLE_CONFIG;
  };

  const getMemberSince = () => {
    if (!profile?.joinedDate) return "N/A";
    return new Date(profile.joinedDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getLastActive = () => {
    if (!profile?.lastActive) return "Recently";
    const date = new Date(profile.lastActive);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="profile-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h2>Profile Not Found</h2>
        <p>{error}</p>
        <Link to="/" className="btn btn-primary">
          Go Home
        </Link>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-empty">
        <FontAwesomeIcon icon={faUserCircle} />
        <h2>No Profile Found</h2>
        <p>The user profile you're looking for doesn't exist.</p>
        <Link to="/" className="btn btn-primary">
          Go Home
        </Link>
      </div>
    );
  }

  const roleConfig = getRoleConfig(profile);
  const avatar = profile.avatar || profile.profileImage || null;
  const backgroundImage = profile.backgroundImage || null;
  const companyLogo = profile.companyLogo || null;
  const companyBanner = profile.companyBanner || null;
  const isSupplier = profile.userType === "supplier";

  return (
    <motion.div
      className="public-profile-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="container">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>

        <div className="profile-card">
          {/* Cover */}
          <div className="profile-cover">
            {backgroundImage ? (
              <img
                src={backgroundImage}
                alt="Profile Cover"
                className="profile-cover-image"
              />
            ) : (
              <div className="profile-cover-gradient" />
            )}
            <div className="profile-cover-overlay" />
          </div>

          {/* Company Logo (Supplier) */}
          {companyLogo && isSupplier && (
            <div className="company-logo-wrapper">
              <img
                src={companyLogo}
                alt={`${profile.name} Logo`}
                className="company-logo"
              />
            </div>
          )}

          {/* Avatar & Basic Info */}
          <div className="profile-header-section">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar">
                {avatar ? (
                  <img src={avatar} alt={profile.name} />
                ) : (
                  <span className="avatar-initials">
                    {getInitials(profile.name)}
                  </span>
                )}
              </div>
              {profile.isApproved === false && (
                <span className="status-badge pending">
                  <FontAwesomeIcon icon={faClock} /> Pending Approval
                </span>
              )}
              {profile.isApproved === true && profile.isTeamMember && (
                <span className="status-badge approved">
                  <FontAwesomeIcon icon={faCheckCircle} /> Verified
                </span>
              )}
            </div>

            <div className="profile-info">
              <h1>{profile.name}</h1>
              <div className="role-badges">
                <span
                  className="role-badge"
                  style={{
                    backgroundColor: roleConfig.color + "20",
                    color: roleConfig.color,
                    borderColor: roleConfig.color + "40",
                  }}
                >
                  <FontAwesomeIcon icon={roleConfig.icon} />
                  {roleConfig.label}
                </span>
                {profile.userType && USER_TYPE_CONFIG[profile.userType] && (
                  <span
                    className="type-badge"
                    style={{
                      backgroundColor:
                        USER_TYPE_CONFIG[profile.userType]?.bgColor,
                      color: USER_TYPE_CONFIG[profile.userType]?.color,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={
                        USER_TYPE_CONFIG[profile.userType]?.icon || faUserCircle
                      }
                    />
                    {USER_TYPE_CONFIG[profile.userType]?.label ||
                      profile.userType}
                  </span>
                )}
              </div>
              <p className="profile-bio">
                {profile.bio ||
                  "Dedicated to serving our farmers and customers."}
              </p>
            </div>

            <div className="profile-actions">
              {profile.email && (
                <a
                  href={`mailto:${profile.email}`}
                  className="action-btn email"
                >
                  <FontAwesomeIcon icon={faEnvelope} /> Email
                </a>
              )}
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="action-btn phone">
                  <FontAwesomeIcon icon={faPhone} /> Call
                </a>
              )}
              {profile.phone && (
                <a
                  href={`https://wa.me/${profile.phone.replace(/[^0-9+]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn whatsapp"
                >
                  <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
                </a>
              )}
              {canEdit && (
                <Link to="/profile" className="action-btn edit">
                  <FontAwesomeIcon icon={faUserCog} /> Edit Profile
                </Link>
              )}
            </div>
          </div>

          {/* Company Banner (Supplier) */}
          {companyBanner && isSupplier && (
            <div className="company-banner-section">
              <img
                src={companyBanner}
                alt={`${profile.name} Banner`}
                className="company-banner"
              />
            </div>
          )}

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <div className="stat-info">
                <span className="stat-label">Member Since</span>
                <span className="stat-value">{getMemberSince()}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="stat-info">
                <span className="stat-label">Last Active</span>
                <span className="stat-value">{getLastActive()}</span>
              </div>
            </div>
            {profile.stats?.totalOrders > 0 && (
              <div className="stat-item">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faShoppingCart} />
                </div>
                <div className="stat-info">
                  <span className="stat-label">Orders</span>
                  <span className="stat-value">
                    {profile.stats.totalOrders}
                  </span>
                </div>
              </div>
            )}
            {profile.stats?.totalPreOrders > 0 && (
              <div className="stat-item">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </div>
                <div className="stat-info">
                  <span className="stat-label">Pre-Orders</span>
                  <span className="stat-value">
                    {profile.stats.totalPreOrders}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Farm Details */}
          {profile.farmDetails && (
            <div className="details-section">
              <h3>
                <FontAwesomeIcon icon={faTractor} /> Farm Details
              </h3>
              <div className="details-grid">
                {profile.farmDetails.farmName && (
                  <div className="detail-item">
                    <span className="detail-label">Farm Name</span>
                    <span className="detail-value">
                      {profile.farmDetails.farmName}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Farm Size</span>
                  <span className="detail-value capitalize">
                    {profile.farmDetails.farmSize || "Small"}
                  </span>
                </div>
                {profile.farmDetails.animalCount > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">Animal Count</span>
                    <span className="detail-value">
                      {profile.farmDetails.animalCount}
                    </span>
                  </div>
                )}
                {profile.farmDetails.yearsInOperation > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">Years in Operation</span>
                    <span className="detail-value">
                      {profile.farmDetails.yearsInOperation}
                    </span>
                  </div>
                )}
                {profile.farmDetails.livestockType?.length > 0 && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Livestock</span>
                    <div className="livestock-tags">
                      {profile.farmDetails.livestockType.map((type) => (
                        <span key={type} className="livestock-tag">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Team Member Details */}
          {profile.isTeamMember && (
            <div className="details-section team-details">
              <h3>
                <FontAwesomeIcon icon={faUsers} /> Team Information
              </h3>
              <div className="team-info-grid">
                <div className="team-info-item">
                  <span className="team-info-label">Role</span>
                  <span className="team-info-value">
                    <FontAwesomeIcon
                      icon={roleConfig.icon}
                      style={{ color: roleConfig.color }}
                    />
                    {roleConfig.label}
                  </span>
                </div>
                {profile.teamRoleDisplay && (
                  <div className="team-info-item">
                    <span className="team-info-label">Position</span>
                    <span className="team-info-value">
                      {profile.teamRoleDisplay}
                    </span>
                  </div>
                )}
                {profile.approvedAt && (
                  <div className="team-info-item">
                    <span className="team-info-label">Approved Since</span>
                    <span className="team-info-value">
                      {new Date(profile.approvedAt).toLocaleDateString(
                        "en-US",
                        { year: "numeric", month: "long", day: "numeric" },
                      )}
                    </span>
                  </div>
                )}
                <div className="team-info-item full-width">
                  <span className="team-info-label">About</span>
                  <p className="team-info-description">
                    {profile.bio ||
                      "Dedicated team member at Kenali Enterprise."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Supplier Details */}
          {profile.supplierInfo && (
            <div className="details-section supplier-details">
              <h3>
                <FontAwesomeIcon icon={faStore} /> Supplier Information
              </h3>
              <div className="supplier-info-grid">
                <div className="supplier-info-item">
                  <span className="supplier-info-label">Company</span>
                  <span className="supplier-info-value">
                    {profile.supplierInfo.companyName || profile.name}
                  </span>
                </div>
                {profile.supplierInfo.supplierId && (
                  <div className="supplier-info-item">
                    <span className="supplier-info-label">Supplier ID</span>
                    <span className="supplier-info-value">
                      {profile.supplierInfo.supplierId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact Section */}
          <div className="contact-section">
            <h3>
              <FontAwesomeIcon icon={faEnvelope} /> Contact Information
            </h3>
            <div className="contact-grid">
              {profile.email && (
                <div className="contact-item">
                  <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                  <div>
                    <span className="contact-label">Email</span>
                    <a
                      href={`mailto:${profile.email}`}
                      className="contact-value"
                    >
                      {profile.email}
                    </a>
                  </div>
                </div>
              )}
              {profile.phone && (
                <div className="contact-item">
                  <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                  <div>
                    <span className="contact-label">Phone</span>
                    <a href={`tel:${profile.phone}`} className="contact-value">
                      {profile.phone}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="profile-footer">
            <div className="footer-actions">
              {isOwner && (
                <Link to="/profile" className="footer-btn primary">
                  <FontAwesomeIcon icon={faUserCog} /> Manage Profile
                </Link>
              )}
              <Link to="/products" className="footer-btn secondary">
                <FontAwesomeIcon icon={faBox} /> Browse Products
              </Link>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-btn whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsappBrand} /> Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PublicProfile;
